// Shared building blocks for the Engagement Intelligence Console.
//
// The single most important rule (contract §3): rates ship as a value plus an
// `*_available` flag. When unavailable (or null) we render "N/A" with a tooltip
// — NEVER 0%. delivery / session-start / conversion rates are always measured,
// so a real 0.0 there renders "0%". <Rate> encapsulates exactly that.

import React, { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { cn } from "../../../lib/utils";

// ── Enum → label maps (contract §4) ─────────────────────────────────────────
export const CHANNEL_LABELS = {
  push: "Push",
  email: "Email",
  sms: "SMS",
  in_app: "Mobile In-App",
};

// Brand-consistent per-channel accent colours (match the reporting palette).
export const CHANNEL_COLORS = {
  push: "#2563eb",
  email: "#7c3aed",
  sms: "#0891b2",
  in_app: "#059669",
};

export const ENGAGEMENT_STATE_LABELS = {
  new_activated: "New User",
  first_convo_done: "First Session Complete",
  active_incomplete_elements: "Active User",
  completed_elements: "Completed Elements",
  engaged: "Engaged User",
  at_risk: "At Risk User",
  dormant: "Dormant User",
  deep_dormant: "Deep Dormant User",
};

export const LIFE_PILLAR_LABELS = {
  mind: "Mind",
  body: "Body",
  relationships_personal: "Personal Relationships",
  relationships_work: "Work Relationships",
  career: "Career",
  financial: "Financial",
  decisions: "Decisions",
  general: "General",
};

export const CONVERSION_TYPE_LABELS = {
  session_started: "Session Started",
  session_completed: "Session Completed",
  element_completed: "Element Completed",
  subscription_upgrade: "Subscription Upgrade",
  invite_sent: "Invite Sent",
  // Reserved placeholders — always 0 today.
  element_started: "Element Started",
  aris_for_two_started: "Aris for Two Started",
  return_user_activation: "Return User Activation",
};

// Conversion types that are reserved placeholders (always 0 currently).
export const RESERVED_CONVERSION_TYPES = new Set([
  "element_started",
  "aris_for_two_started",
  "return_user_activation",
]);

export const DELIVERY_STATUS_STYLES = {
  delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
  sent: "bg-sky-50 text-sky-700 border-sky-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  skipped: "bg-slate-100 text-slate-600 border-slate-200",
  suppressed: "bg-amber-50 text-amber-700 border-amber-200",
};

export const labelFor = (map, key) => map[key] || key;

// ── Formatters ───────────────────────────────────────────────────────────────
export const fmtInt = (n) =>
  n == null ? "—" : Number(n).toLocaleString("en-US");

// A value already expressed as a percentage number (e.g. conversion_pct: 14.1).
export const fmtPctValue = (v, decimals = 1) =>
  v == null ? "—" : `${Number(v).toFixed(decimals)}%`;

// avg open→conversion latency: seconds → human "Xs / Xm / Xh".
export const fmtDuration = (sec) => {
  if (sec == null) return "—";
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) return `${(sec / 60).toFixed(1)}m`;
  return `${(sec / 3600).toFixed(1)}h`;
};

// ── The N/A rule, encapsulated (contract §3) ─────────────────────────────────
const NA_HINT = {
  open: "Open tracking not yet enabled for this channel.",
  click: "Click tracking not yet enabled for this channel.",
  engagement: "Engagement (opens/delivered) not measured for this channel.",
  default: "Not measured yet for this channel.",
};

/**
 * Render a fractional rate (0..1) as a percentage, applying the N/A rule.
 * @param {number|null} value fraction in [0,1]
 * @param {boolean} available the matching `*_available` flag
 * @param {"open"|"click"|"engagement"|"default"} hint tooltip copy when N/A
 */
export const Rate = ({ value, available, hint = "default", decimals = 0, className }) => {
  const measured = available !== false && value != null;
  if (!measured) {
    return (
      <span
        className={cn("cursor-help text-muted-foreground/70", className)}
        title={NA_HINT[hint] || NA_HINT.default}
      >
        N/A
      </span>
    );
  }
  return (
    <span className={cn("tabular-nums", className)}>
      {(value * 100).toFixed(decimals)}%
    </span>
  );
};

/** Small coloured channel chip. */
export const ChannelBadge = ({ channel }) => (
  <span
    className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium"
    style={{
      borderColor: `${CHANNEL_COLORS[channel] || "#94a3b8"}40`,
      color: CHANNEL_COLORS[channel] || "#475569",
      backgroundColor: `${CHANNEL_COLORS[channel] || "#94a3b8"}12`,
    }}
  >
    <span
      className="h-1.5 w-1.5 rounded-full"
      style={{ backgroundColor: CHANNEL_COLORS[channel] || "#94a3b8" }}
    />
    {CHANNEL_LABELS[channel] || channel}
  </span>
);

/** Delivery-status pill. */
export const StatusBadge = ({ status }) => (
  <span
    className={cn(
      "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize",
      DELIVERY_STATUS_STYLES[status] || "bg-slate-100 text-slate-600 border-slate-200"
    )}
  >
    {status || "—"}
  </span>
);

/** Yes/No dot used for opened / clicked / session / converted columns. */
export const BoolDot = ({ value }) => (
  <span
    className={cn(
      "inline-block h-2.5 w-2.5 rounded-full",
      value ? "bg-emerald-500" : "bg-slate-200"
    )}
    title={value ? "Yes" : "No"}
  />
);

/** Active-window pill — always surfaced so users know the date filter (§2),
 *  plus the timezone the backend actually bucketed in (§3.5). */
export const WindowBadge = ({ window, timezone }) =>
  window || timezone ? (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 shadow-sm">
      {window ? (
        <>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Window
          </span>
          {window}
        </>
      ) : null}
      {timezone ? (
        <>
          <span className="text-slate-300">·</span>
          <Clock className="h-3 w-3 text-muted-foreground" />
          <span className="text-muted-foreground">{timezone}</span>
        </>
      ) : null}
    </span>
  ) : null;

// ── Loading / error / empty shells ───────────────────────────────────────────
export const ErrorBox = ({ children }) => (
  <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
    {children}
  </div>
);

export const EmptyState = ({ children }) => (
  <p className="py-8 text-center text-sm text-muted-foreground">{children}</p>
);

// ── Data-fetch hook shared by every dashboard tab ────────────────────────────
// Handles loading/error and ignores stale responses when deps change fast.
export function useEngagementData(fetcher, deps) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    fetcher()
      .then((d) => alive && setData(d))
      .catch((e) =>
        alive && setError(e?.response?.data?.message || "Failed to load data.")
      )
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error };
}
